
import moment from 'moment'
import Vue from 'vue'
import api from '../../api/api'

import Breadcrumbs from '../../components/Breadcrumbs.vue'
import SpinnerLoader from '../../components/SpinnerLoader.vue'
import BaseInputOutlined from '../../components/inputs/BaseInputOutlined.vue'
import CustomActivatorSelect from '../../components/inputs/CustomActivatorSelect.vue'
import VideoHelper from '@root/src/components/VideoHelper.vue'
import DaysTab from '@/components/chisai/GCB2/sameDayRec/DaysTab.vue'
import MastersTab from '@/components/chisai/GCB2/sameDayRec/MastersTab.vue'
import CrossTab from '@/components/chisai/GCB2/sameDayRec/CrossTab.vue'
import KpiTab from '@/components/chisai/GCB2/sameDayRec/KpiTab.vue'
import KpiSettingsTab from '@/components/chisai/GCB2/sameDayRec/KpiSettingsTab.vue'
import Opz2Page from '@/components/chisai/GCB2/sameDayRec/v3/Opz2Page.vue'
import TrialOfferModal from '@/modules/marketplace/components/TrialOfferModal.vue'
import { findTrialOffer, trialPeriodLabel } from '@/modules/marketplace/trialOffers'
import { activateIntegrationTrial } from '@/api/marketplace'
import { useMarketplaceStore } from '@/store/stores/marketplace'
import { useBillingStore } from '@/store/stores/billing'
import { isBlockedByDoctorBotTariff } from '@/helpers/marketplace/dhChatVisibility'
import { hasOpz2Access } from '@/helpers/gcb2/opz2Access'
import { checkRole } from '@/helpers/roles'
import config from '@root/config'
import { MarketplaceIntegration } from '@/types/marketplace'
import { Connector, Project, ProjectType } from '../../types/main'
import { CONNECTOR_TYPES } from '../../vars/general'
import pickBy from 'lodash/pickBy'
import isObject from 'lodash/isObject'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import setWith from 'lodash/setWith'

type TabId = 'days' | 'masters' | 'cross' | 'kpi' | 'kpiSettings'

export default Vue.extend({
    name: 'GCB2SameDayRecReport',
    components: {
        Breadcrumbs,
        SpinnerLoader,
        CustomActivatorSelect,
        BaseInputOutlined,
        VideoHelper,
        DaysTab,
        MastersTab,
        CrossTab,
        KpiTab,
        KpiSettingsTab,
        Opz2Page,
        TrialOfferModal,
    },
    data: () => ({
        pageLoading: false,
        dashboardDataLoading: true,
        dateMenu: false,
        dates: [] as string[],
        dateFormat: 'DD.MM.YYYY',
        datePickerDateFormat: 'YYYY-MM-DD',
        filters: {} as any,
        filtersLists: {} as any,
        inititalFilters: {},
        selectedProjectId: null as null | string,
        selectedProjectCrm: null as null | string,
        dashboardData: {} as any,
        showVideoHelp: true,
        activeTab: 'days' as TabId,
        appliedFilters: {} as any,
        tabLoading: {} as Record<string, boolean>,
        trialOpen: false,
        activatingTrial: false,
    }),
    computed: {
        project(): Project {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id)
        },
        projectType(): ProjectType {
            return this.$store.getters.projectType
        },
        pseudoNetworkProjects(): Project[] {
            return this.$store.getters.pseudoNetworkProjects
        },
        pseudoNetworkOptions(): Array<{ id: string; name: string }> {
            return (this.pseudoNetworkProjects ?? []).map((p: any) => ({ id: p.id, name: p.name }))
        },
        crmLabel(): string {
            const types = (this.project.connectors ?? []).map((c: any) => c.connectorType)
            if (types.includes(CONNECTOR_TYPES.yClients)) return 'YCLIENTS'
            if (types.includes(CONNECTOR_TYPES.altegio)) return 'Altegio'
            return 'CRM'
        },
        isPseudoNetwork(): boolean {
            return this.projectType === 'pseudo_network'
        },
        projectIsNetwork(): boolean {
            return this.projectType === 'network'
        },
        reportProjectId(): string {
            return this.isPseudoNetwork && this.selectedProjectId
                ? this.selectedProjectId
                : this.project.id
        },
        connector(): Connector | undefined {
            return this.project.connectors.find(el => el.connectorType === CONNECTOR_TYPES.GCB2)
        },
        isVideoHintHidden(): boolean {
            return this.connector?.videoHintDisabled
        },
        /** Права проверяются раньше тарифа и доступа к модулю. */
        hasView(): boolean {
            return Boolean(this.project?.id && checkRole(this.project.id, 'gcb2:sameDayRec:view'))
        },
        /** Доступ к живым вкладкам 2.0 — по флагу проекта в Firebase. */
        hasModule(): boolean {
            return hasOpz2Access(this.project)
        },
        isDoctorBot(): boolean {
            const tariff = useBillingStore().getCurrentTariff(this.project?.id)
            if (!tariff) return false
            return isBlockedByDoctorBotTariff({
                baseTariffId: tariff.baseTariffId,
                currentTariffId: tariff.integrationId,
            })
        },
        showDaysTab(): boolean {
            return !this.isDoctorBot
        },
        showOpz2Head(): boolean {
            return this.hasModule || this.activeTab !== 'days'
        },
        showLockedPreview(): boolean {
            return !this.hasModule && this.activeTab !== 'days'
        },
        opz2Trial() {
            return findTrialOffer('opz2')
        },
        trialButtonText(): string {
            const days = this.opz2Trial?.days || 5
            return `Попробовать ${trialPeriodLabel(days)}`
        },
        opz2Integration(): MarketplaceIntegration | null {
            const moduleId = config.marketplaceModulesIds[config.buildType]?.opz2
            if (!moduleId || !this.project?.id) return null
            const integrations = useMarketplaceStore().allIntgrations(this.project.id) as MarketplaceIntegration[]
            return integrations.find(item => String(item.id) === String(moduleId)) ?? null
        },
        showTrialButton(): boolean {
            const integration = this.opz2Integration
            return Boolean(integration && integration.isTrialUsed !== true)
        },
        /**
         * Вкладки 2.0 видны всегда. «По дням» скрыта на тарифе Доктор-бот (tariff1).
         */
        visibleTabs(): { id: TabId; label: string }[] {
            const tabs: { id: TabId; label: string }[] = [
                { id: 'days', label: 'По дням' },
                { id: 'masters', label: 'ОПЗ 2.0' },
                { id: 'cross', label: 'Кросс-продажи' },
                { id: 'kpi', label: 'KPI' },
                { id: 'kpiSettings', label: 'Настройка KPI' },
            ]
            return this.isDoctorBot ? tabs.filter(tab => tab.id !== 'days') : tabs
        },
        activeTabLoading(): boolean {
            if (this.activeTab === 'days') return this.dashboardDataLoading
            return Boolean(this.tabLoading[this.activeTab])
        },
        breadcrumbs(): Array<any> {
            return [
                { text: this.project.name, to: `/project/${this.project.id}` },
                { text: 'ОПЗ', to: '', disabled: true },
            ]
        },
        dateRangeText(): string {
            return this.sortedDates
                .map(date => moment(date, this.datePickerDateFormat).format(this.dateFormat))
                .join(' - ')
        },
        sortedDates(): string[] {
            return [
                ...this.dates.sort(
                    (a, b) =>
                        moment(a, this.datePickerDateFormat).unix() -
                        moment(b, this.datePickerDateFormat).unix()
                ),
            ]
        },
        displayFilters(): any {
            return {
                filialList: Object.values(this.filters.filialList ?? {})
                    .filter((el: any) => el.selected)
                    .map((el: any) => el.id),
            }
        },
        queryFilters(): any {
            return pickBy(
                {
                    dateFrom: this.sortedDates[0],
                    dateTo: this.sortedDates[1] ?? this.sortedDates[0],
                    filialList: this.projectIsNetwork ? this.filters.filialList : undefined,
                },
                (v, k) => {
                    if (isObject(v) && isEmpty(v)) {
                        return false
                    }
                    if (
                        !isNil(v) &&
                        this.displayFilters[k] &&
                        (this.filtersLists[k].length === this.displayFilters[k].length ||
                            this.displayFilters[k].length === 0)
                    ) {
                        return false
                    }
                    return true
                }
            )
        },
    },
    watch: {
        isDoctorBot(blocked: boolean) {
            if (blocked && this.activeTab === 'days') this.activeTab = 'masters'
        },
        hasModule(active: boolean) {
            if (!this.hasView || active) return
            this.loadTrialStatus()
        },
    },
    methods: {
        closeTrial() {
            if (this.activatingTrial) return
            this.trialOpen = false
        },
        async ensureTariff() {
            const projectId = this.project?.id
            if (!projectId) return
            const billing = useBillingStore()
            if (!billing.getCurrentTariff(projectId)) {
                await billing.fetchCurrentTariff(projectId)
            }
        },
        async loadTrialStatus() {
            const projectId = this.project?.id
            if (!projectId) return
            await useMarketplaceStore().fetchIntegrations(projectId)
        },
        async activateTrial() {
            const integrationId = this.opz2Integration && this.opz2Integration.id
            const projectId = this.project?.id
            if (this.activatingTrial || !projectId || !integrationId) return
            this.activatingTrial = true
            const { error } = await activateIntegrationTrial({
                projectId: String(projectId),
                integrationId: String(integrationId),
            })
            this.activatingTrial = false
            if (error) {
                this.$store.dispatch('callNotify', 'Не удалось активировать пробный период')
                return
            }
            this.trialOpen = false
            await useMarketplaceStore().fetchIntegrations(String(projectId))
            await this.$store.dispatch('project/fetchProject', projectId)
        },
        buyModule() {
            const moduleId = config.marketplaceModulesIds[config.buildType]?.opz2
            if (!moduleId || !this.project?.id) return
            this.$router
                .push(`/project/${this.project.id}/marketplace/integration/${moduleId}`)
                .catch(() => {})
        },
        setTabLoading(tab: string, value: boolean) {
            this.$set(this.tabLoading, tab, value)
        },
        setFiltersLists() {
            this.filtersLists = {
                filialList: Object.values(this.filters.filialList ?? {}).map((el: any) => ({
                    id: el.id,
                    name: el.option,
                })),
            }
        },
        /** Значение по умолчанию — последние 30 дней (US2S2). */
        setDefaultDates() {
            this.dates = [
                moment()
                    .subtract(30, 'days')
                    .format(this.datePickerDateFormat),
                moment().format(this.datePickerDateFormat),
            ]
        },
        dropFilters() {
            this.setDefaultDates()
            this.filters = Object.assign({}, this.inititalFilters)
            this.initData(true)
        },
        setFilterValue(filterId: string, val: any) {
            const updatedFilterVal = {}
            const selectedHmap = val.reduce((a: any, c: any) => Object.assign(a, { [c]: true }), {})
            this.filtersLists[filterId].forEach((el: any) => {
                setWith(
                    updatedFilterVal,
                    [el.id],
                    {
                        id: el.id,
                        option: el.name,
                        selected: Boolean(selectedHmap[el.id]),
                        service_group: el.service_group,
                    },
                    Object
                )
            })
            this.filters = Object.assign({}, this.filters, { [filterId]: updatedFilterVal })
        },
        applyFilters() {
            this.initData(false)
        },
        async initData(reset: boolean) {
            if (!this.hasView) return
            this.dashboardDataLoading = true
            if (reset) {
                this.selectedProjectId = this.project.id
            }
            if (this.isPseudoNetwork) {
                const data: any = (
                    await api.project.getCrmType({ projectId: this.selectedProjectId })
                ).data
                this.selectedProjectCrm = data?.type
            }

            // Фильтры фиксируются в момент «Применить»: вкладки 2.0 перечитывают
            // данные по изменению appliedFilters, каждая своим запросом.
            this.appliedFilters = { ...this.queryFilters }

            const { data, error } = await api.gcb2.getSameDayRecReportData({
                projectId: this.reportProjectId,
                filters: this.queryFilters,
            })
            if (error) {
                this.$store.dispatch('callNotify', 'Ошибка')
            }
            this.dashboardData = data?.reportData ?? {}
            this.filters = Object.assign({}, this.filters, data?.filters)
            this.setFiltersLists()
            this.dashboardDataLoading = false
        },
        setVideoHelepVisible(value: boolean) {
            this.showVideoHelp = value
        },
    },
    async mounted() {
        if (!this.hasView) return
        this.inititalFilters = Object.assign({}, this.filters)
        this.pageLoading = true
        this.setDefaultDates()
        // ОПЗ 2.0 грузит свой отчёт сам; прежний запрос нужен ей только ради списка филиалов сети
        if (!this.hasModule || this.projectIsNetwork || this.isPseudoNetwork) {
            await Promise.all([this.initData(true), this.ensureTariff()])
        } else {
            this.selectedProjectId = this.project.id
            await this.ensureTariff()
        }
        if (this.isDoctorBot && this.activeTab === 'days') this.activeTab = 'masters'
        if (!this.hasModule) await this.loadTrialStatus()
        this.pageLoading = false
    },
})
